/**
 * +lead: rotation rework (F1, F2, F11). Pure, read-only hotbar helpers for the HUD, bots and harness.
 * No RNG, no mutation, no per-frame cost beyond one pass over the unit's (and its target's) auras.
 */
import type { AbilityId, AuraDef, AuraInstance, ClassDef, ContentRegistry, DeepReadonly, HotbarKey, Unit, UnitId, World } from '../contracts';
import { BIND_KEYS, EXTRA_BIND_KEYS, HOTBAR_KEYS } from '../contracts';
import { resolveOverride } from './casting';

type Content = Pick<ContentRegistry, 'ability' | 'aura'>;
type RWorld = DeepReadonly<World> | World;
type RUnit = DeepReadonly<Unit>;

/** Every bound key of a class (binds + extraBinds), in HOTBAR_KEYS order. Unbound keys are absent. */
export function classHotbar(def: Pick<ClassDef, 'binds' | 'extraBinds'>): Partial<Record<HotbarKey, AbilityId>> {
  const out: Partial<Record<HotbarKey, AbilityId>> = {};
  for (const k of HOTBAR_KEYS) {
    const id = (BIND_KEYS as readonly string[]).includes(k)
      ? def.binds[k as (typeof BIND_KEYS)[number]]
      : (EXTRA_BIND_KEYS as readonly string[]).includes(k)
        ? def.extraBinds?.[k as (typeof EXTRA_BIND_KEYS)[number]]
        : undefined;
    if (id) out[k] = id;
  }
  return out;
}

/** What a press of `abilityId` casts right now for `unitId` (the override, or the id itself). */
export function resolveAbilityId(content: Pick<ContentRegistry, 'ability'>, world: RWorld, unitId: UnitId, abilityId: AbilityId): AbilityId {
  return resolveOverride(content, world.units[unitId] as RUnit | undefined, abilityId);
}

/** The unit's live replacement map (base → override). Empty object when nothing is transformed. */
export function abilityOverridesOf(world: RWorld, unitId: UnitId): Readonly<Record<AbilityId, AbilityId>> {
  return (world.units[unitId]?.abilityOverrides as Record<AbilityId, AbilityId> | undefined) ?? {};
}

/** Ability ids a proc aura lights up automatically: every ability scoped by one of its mods. */
function autoGlowIds(def: AuraDef): AbilityId[] {
  const out: AbilityId[] = [];
  for (const m of def.mods ?? []) for (const id of m.abilityIds ?? []) if (!out.includes(id)) out.push(id);
  return out;
}

function addGlow(set: Set<AbilityId>, def: AuraDef, inst: DeepReadonly<AuraInstance>, ownAura: boolean): void {
  const glow = def.ui?.glow;
  if (glow === false) return;
  if (glow) {
    if (inst.stacks >= (glow.atStacks ?? 1)) for (const id of glow.abilityIds) set.add(id);
  } else if (ownAura && def.ui?.proc) {
    for (const id of autoGlowIds(def)) set.add(id);
  }
  // A transformed button glows (both the base id and its form).
  if (ownAura) {
    for (const o of def.overrides ?? []) {
      set.add(o.abilityId);
      set.add(o.with);
    }
  }
}

/**
 * Spell-activation glow (F11): ability ids that should glow for `unitId` now.
 * - Auras on the unit: an explicit `ui.glow` at `atStacks` (default 1); else, for `ui.proc` auras,
 *   every ability their mods are scoped to; plus both ids of every active override.
 * - Debuffs the unit cast on its current target: an explicit `ui.glow` at `atStacks` (Wounds ≥ 4 → MS).
 * - `ui.glow: false` suppresses all of the above for that aura.
 * When a glowing id is the current form of a transformed button, the base (bound) id is included too,
 * so the HUD can test `has(boundId)`.
 */
export function activeHighlights(content: Content, world: RWorld, unitId: UnitId): Set<AbilityId> {
  const set = new Set<AbilityId>();
  const u = world.units[unitId];
  if (!u?.alive) return set;
  for (const inst of u.auras) {
    const def = content.aura(inst.auraId);
    if (def && (def.ui || def.overrides)) addGlow(set, def, inst, true);
  }
  const t = u.targetId ? world.units[u.targetId] : undefined;
  if (t?.alive && t.id !== u.id) {
    for (const inst of t.auras) {
      if (inst.casterId !== unitId) continue;
      const def = content.aura(inst.auraId);
      if (def?.ui?.glow) addGlow(set, def, inst, false);
    }
  }
  const ov = u.abilityOverrides;
  if (ov) for (const base of Object.keys(ov)) if (set.has(ov[base]!)) set.add(base);
  return set;
}

/**
 * Stack counts to draw on buttons (F11 `ui.stackCounterOn`): buffs on the unit, and debuffs the unit
 * cast on its current target. Keys include the base id of a transformed button.
 */
export function stackCounters(content: Content, world: RWorld, unitId: UnitId): Record<AbilityId, number> {
  const out: Record<AbilityId, number> = {};
  const u = world.units[unitId];
  if (!u?.alive) return out;
  const add = (def: AuraDef | undefined, stacks: number): void => {
    for (const id of def?.ui?.stackCounterOn ?? []) out[id] = (out[id] ?? 0) + stacks;
  };
  for (const inst of u.auras) {
    const def = content.aura(inst.auraId);
    if (def?.kind === 'buff') add(def, inst.stacks);
  }
  const t = u.targetId ? world.units[u.targetId] : undefined;
  if (t?.alive && t.id !== u.id) {
    for (const inst of t.auras) {
      if (inst.casterId !== unitId) continue;
      const def = content.aura(inst.auraId);
      if (def?.kind === 'debuff') add(def, inst.stacks);
    }
  }
  const ov = u.abilityOverrides;
  if (ov) for (const base of Object.keys(ov)) if (out[ov[base]!] !== undefined && out[base] === undefined) out[base] = out[ov[base]!]!;
  return out;
}
